import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkle, X, PaperPlaneRight, Lightning, ClockCounterClockwise, NotePencil, ChatText, CaretLeft } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { API } from "../lib/api";

const AI_AVATAR = "https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?crop=entropy&cs=srgb&fm=jpg&q=85&w=200";

const SUGGESTIONS = [
  "Summarize our financial health",
  "Which items are low on stock?",
  "Who are our top customers by balance?",
  "How much is outstanding in receivables?",
];

const MD_COMPONENTS = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  h1: ({ node, ...props }) => <h1 className="text-base font-bold mt-3 mb-1.5 first:mt-0" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-sm font-bold mt-3 mb-1.5 first:mt-0" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-sm font-semibold mt-2.5 mb-1 first:mt-0" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-0.5" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node, ...props }) => <a className="text-primary underline underline-offset-2" target="_blank" rel="noopener noreferrer" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-primary/40 pl-3 italic text-muted-foreground my-2" {...props} />,
  hr: ({ node, ...props }) => <hr className="my-3 border-border" {...props} />,
  code: ({ node, className, children, ...props }) => (
    <code className={`${className || ""} ai-code bg-foreground/10 rounded px-1 py-0.5 text-[0.8125rem] font-mono`} {...props}>{children}</code>
  ),
  pre: ({ node, ...props }) => <pre className="block bg-foreground/[0.06] rounded-sm p-2.5 my-2 text-[0.8125rem] font-mono overflow-x-auto" {...props} />,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table className="w-full text-[0.8125rem] border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-foreground/[0.06]" {...props} />,
  th: ({ node, ...props }) => <th className="border border-border px-2 py-1 text-left font-semibold" {...props} />,
  td: ({ node, ...props }) => <td className="border border-border px-2 py-1" {...props} />,
};

function Markdown({ content }) {
  return (
    <div className="ai-markdown" data-testid="ai-message-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]} components={MD_COMPONENTS}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export default function AIAssistant({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(newId);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ai/sessions`, { credentials: "include" });
      if (res.ok) setSessions(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (open) loadSessions();
  }, [open, loadSessions]);

  const newChat = () => {
    setMessages([]);
    setSessionId(newId());
    setShowHistory(false);
    setInput("");
  };

  const openSession = async (sid) => {
    try {
      const res = await fetch(`${API}/ai/history?session_id=${encodeURIComponent(sid)}`, { credentials: "include" });
      const hist = res.ok ? await res.json() : [];
      setMessages(hist.map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
    } catch {
      setMessages([]);
    }
    setShowHistory(false);
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: content, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) {
              setMessages((m) => {
                const last = m[m.length - 1];
                return [...m.slice(0, -1), { ...last, content: last.content + evt.delta }];
              });
            } else if (evt.error) {
              setMessages((m) => {
                const last = m[m.length - 1];
                return [...m.slice(0, -1), { ...last, content: last.content + `\n\n_Error: ${evt.error}_` }];
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const last = m[m.length - 1];
        return [...m.slice(0, -1), { ...last, content: last.content + `\n\n_Connection error._` }];
      });
    } finally {
      setStreaming(false);
      loadSessions();
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />
      <aside
        data-testid="ai-assistant-panel"
        className={`fixed inset-y-0 right-0 w-full sm:w-[440px] bg-background border-l border-border z-50 flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={AI_AVATAR} alt="" className="h-10 w-10 rounded-sm object-cover ring-2 ring-white/30" />
            <div>
              <p className="font-bold tracking-tight flex items-center gap-1.5">
                Atlas <Sparkle size={14} weight="fill" />
              </p>
              <p className="text-xs text-white/70">AI Financial Controller</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={newChat}
              data-testid="ai-new-chat"
              title="New chat"
              className="p-1.5 rounded-sm hover:bg-white/15 transition-colors duration-150"
            >
              <NotePencil size={20} />
            </button>
            <button
              onClick={() => { setShowHistory((v) => !v); if (!showHistory) loadSessions(); }}
              data-testid="ai-history-toggle"
              title="Conversation history"
              className={`p-1.5 rounded-sm transition-colors duration-150 ${showHistory ? "bg-white/20" : "hover:bg-white/15"}`}
            >
              <ClockCounterClockwise size={20} />
            </button>
            <button onClick={onClose} data-testid="close-ai-assistant" className="p-1.5 rounded-sm hover:bg-white/15 transition-colors duration-150">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* History drawer */}
        {showHistory && (
          <div data-testid="ai-history-panel" className="absolute inset-x-0 top-[72px] bottom-0 bg-background z-10 flex flex-col animate-fade-up">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <button onClick={() => setShowHistory(false)} data-testid="ai-history-back" className="p-1 -ml-1 rounded-sm hover:bg-secondary transition-colors duration-150">
                <CaretLeft size={18} />
              </button>
              <p className="font-semibold text-sm">Conversations</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground px-2 py-4">No past conversations yet. Start chatting and they'll appear here.</p>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    data-testid="ai-session-item"
                    className={`w-full text-left px-3 py-2.5 rounded-sm border transition-colors duration-150 flex items-start gap-2.5 ${
                      s.session_id === sessionId ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary"
                    }`}
                  >
                    <ChatText size={16} className="text-primary shrink-0 mt-0.5" weight="fill" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">{s.title}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{timeAgo(s.last_at)} · {s.count} message{s.count === 1 ? "" : "s"}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="animate-fade-up">
              <p className="text-sm text-muted-foreground leading-relaxed">
                I read your live ledgers, inventory and sales. Ask me anything about the business.
              </p>
              <div className="mt-4 space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                    className="w-full text-left text-sm px-3 py-2.5 rounded-sm border border-border hover:border-primary hover:bg-primary/5 transition-colors duration-150 flex items-center gap-2"
                  >
                    <Lightning size={15} className="text-primary shrink-0" weight="fill" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3.5 py-2.5 rounded-sm text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-foreground border border-border"
                }`}
              >
                {m.role === "assistant" ? (
                  <Markdown content={m.content || ""} />
                ) : (
                  m.content
                )}
                {m.role === "assistant" && streaming && i === messages.length - 1 && !m.content && (
                  <span className="cursor-blink">▍</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-end gap-2 border border-border rounded-sm p-1.5 focus-within:border-primary transition-colors duration-150"
          >
            <textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder="Ask Atlas about your business…"
              className="flex-1 resize-none bg-transparent text-sm px-2 py-1.5 outline-none max-h-32"
            />
            <button
              type="submit" disabled={streaming || !input.trim()} data-testid="ai-send-button"
              className="h-8 w-8 shrink-0 bg-primary text-primary-foreground rounded-sm flex items-center justify-center disabled:opacity-40 hover:bg-primary/90 transition-colors duration-150"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </form>
        </div>
      </aside>
    </>
  );
}
